import type { SearchResult } from "../api-models";
import { docTarget, type DocTarget } from "./docRoutes";

/**
 * Where a global-search hit opens, and what it is called in the palette.
 *
 * Most kinds are already DocType constants, so DOC_ROUTES (docRoutes.ts) resolves them with
 * no second table to keep in step — the server deliberately emits those constants as its
 * `kind`. Only the masters need anything here, and they split two ways: the few with a real
 * detail route, and the rest, which have no page of their own and open their register
 * pre-filtered by the term. That works because every one of those registers is a ListPage,
 * whose search box reads straight off the URL.
 */

/** Group headings, in the order the palette shows them. */
export const KIND_LABELS: Record<string, string> = {
  ITEM: "Items",
  PROJECT: "Projects",
  SITE: "Sites",
  SUPPLIER: "Suppliers",
  BOAT: "Boats",
  VEHICLE: "Vehicles",
  STAFF: "Staff",
  MRQ: "Material Requests",
  GIN: "Goods Issues",
  BOAT_NOTE: "Boat Notes",
  MRN: "Receipts",
  CONSUMPTION: "Consumption",
  TRANSFER_ORDER: "Transfers",
  SITE_RETURN: "Site Returns",
  SUPPLIER_RETURN: "Supplier Returns",
  STOCK_ADJUSTMENT_SITE: "Stock Adjustments",
  TOOL_CUSTODY: "Tool Custody",
  PR: "Purchase Requisitions",
  RFQ: "Quotation Requests",
  PO: "Purchase Orders",
  PO_AMENDMENT: "PO Amendments",
  GRN: "Goods Receipts",
  GIN_DIRECT: "GIN Direct",
  BOM_VERSION: "BOM Versions",
  BOM_VARIATION: "BOM Variations",
  ATTENDANCE_SHEET: "Attendance",
  SERVICE_REQUEST: "Service Requests",
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind.replaceAll("_", " ");
}

/** Masters reached by their own detail route, the same shape as a document. */
const MASTER_DETAIL_ROUTES: Record<string, DocTarget> = {
  PROJECT: { path: "/projects", permission: "Projects.Read" },
  STAFF: { path: "/staff", permission: "Staff.Read" },
};

/**
 * Masters with no detail page. The register is the destination and the hit's own code is
 * the filter, so the row the user picked is the one row on screen when they arrive.
 */
const MASTER_REGISTER_ROUTES: Record<string, DocTarget> = {
  ITEM: { path: "/masters/items", permission: "Items.Read" },
  SUPPLIER: { path: "/masters/suppliers", permission: "Suppliers.Read" },
  BOAT: { path: "/masters/boats", permission: "Boats.Read" },
  VEHICLE: { path: "/vehicles", permission: "Vehicles.Read" },
};

/**
 * Where a hit opens, or null for a kind with nowhere to land. The permission comes back
 * with it so a caller can fall back to plain text rather than link into a 403 — the server
 * has already scoped the results, so that is a backstop, not the gate.
 */
export function searchTarget(hit: SearchResult): DocTarget | null {
  const detail = MASTER_DETAIL_ROUTES[hit.kind];
  if (detail) return { path: `${detail.path}/${hit.id}`, permission: detail.permission };

  const register = MASTER_REGISTER_ROUTES[hit.kind];
  if (register) {
    // The code is the narrowest thing the register can filter on; a supplier has none, so
    // its name does the job instead.
    const term = hit.code || hit.label;
    return { path: `${register.path}?search=${encodeURIComponent(term)}`, permission: register.permission };
  }

  // A site has no page of its own — it is reached through the project that owns it, which
  // the server sends as the hit's projectId.
  if (hit.kind === "SITE") {
    return hit.projectId ? { path: `/projects/${hit.projectId}`, permission: "Projects.Read" } : null;
  }

  return docTarget(hit.kind, hit.id);
}

/** The line a hit is identified by: the code where there is one, the name otherwise. */
export function hitTitle(hit: SearchResult): string {
  return hit.code || hit.label || `#${hit.id}`;
}

/** The supporting line, dropped when it would only repeat the title. */
export function hitSubtitle(hit: SearchResult): string | null {
  const sub = hit.code ? hit.label : "";
  const text = (sub || hit.sub || "").trim();
  return text && text !== hitTitle(hit) ? text : null;
}
